import { defineStore } from 'pinia'
import { ref, computed, Component } from 'vue'
import { useSessionStore } from '@/stores/components/sessionStore'
import router from '@/router'
import { EmpresaStatusDocumentacaoEnum } from '@/enums/EmpresaStatusDocumentacaoEnum'
import { ModulosEnum } from '@/enums/ModulosEnum'
import { AlertTriangle, CircleAlert, CircleCheckBig, AlertCircleIcon, Clock } from 'lucide-vue-next'
import CampanhaBanner from '@/views/Banners/CampanhaBanner.vue'
import type { Campaings } from '@/types/plugins/session'
import moment from 'moment'

// Nomes dos meses em português
const mesesPorExtenso = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro'
]

/**
 * Calcula a próxima data de expiração da assinatura baseado na regra de renovação
 * @param dataCreated Data de criação da assinatura
 * @returns Próxima data de expiração
 */
function calcularDataExpiracao(dataCreated: string | undefined): moment.Moment | null {
  if (!dataCreated) return null

  const dataCriacao = moment(dataCreated)

  // O dia de cobrança é fixo (dia da criação da assinatura). Verifica se a assinatura foi
  // criada no último dia do seu mês, para poder reposicionar corretamente no mês seguinte.
  const diaOriginal = dataCriacao.date()
  const ultimoDiaDoMes = dataCriacao.clone().endOf('month').date()
  const ehUltimoDiaDoMes = diaOriginal === ultimoDiaDoMes

  // A expiração/cancelamento é sempre a partir de hoje + 1 mês, no mesmo dia da assinatura
  // (ou no último dia do mês seguinte, caso a assinatura tenha sido criada no último dia do mês) —
  // nunca uma data baseada na criação original, que poderia já estar no passado.
  let proximalData = moment().add(1, 'month')

  if (ehUltimoDiaDoMes) {
    proximalData = proximalData.endOf('month')
  } else {
    // Limita ao último dia do mês de destino, evitando que .date() estoure para o mês seguinte
    // quando esse mês tiver menos dias que o dia original (ex.: dia 31 em um mês de 30 dias).
    const ultimoDiaDoMesDestino = proximalData.clone().endOf('month').date()
    proximalData = proximalData.date(Math.min(diaOriginal, ultimoDiaDoMesDestino))
  }

  return proximalData
}

/**
 * Formata a data de expiração no padrão "DD de mesporescrito de AAAA"
 * @param data Data a ser formatada
 * @returns String formatada
 */
function formatarDataExpiracao(data: moment.Moment): string {
  const dia = data.format('DD')
  const mes = mesesPorExtenso[data.month()]
  const ano = data.format('YYYY')
  return `${dia} de ${mes} de ${ano}`
}

/**
 * Calcula os dias restantes desde hoje até a data de expiração
 * @param dataExpiracao Data de expiração
 * @returns Número de dias restantes
 */
function calcularDiasRestantes(dataExpiracao: moment.Moment): number {
  const hoje = moment().startOf('day')
  const expiracao = dataExpiracao.startOf('day')
  return expiracao.diff(hoje, 'days')
}

type CampaignPrimaryAction = {
  primaryLabel: string
  onPrimaryAction: () => void
  campaingModuleCode?: string
}

export interface GlobalNotificationAction {
  label: string
  action: () => void
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger'
}

export interface GlobalNotification {
  id: string
  message?: string
  sub_message?: string
  severity?: 'success' | 'info' | 'warn' | 'error'
  icon?: string | Component
  persistent?: boolean
  pushLayout?: boolean
  condition?: () => boolean
  actions?: GlobalNotificationAction[]
  customComponent?: Component | undefined
  customComponentProps?: Record<string, unknown>
}

export const useGlobalNotificationStore = defineStore(
  'globalNotification',
  () => {
    const sessionStore = useSessionStore()
    const staticNotifications = ref<GlobalNotification[]>([])
    const dismissedNotifications = ref<string[]>([])

    function resolveMaybeRef<T>(maybeRef: unknown): T | null {
      if (maybeRef && typeof maybeRef === 'object' && 'value' in (maybeRef as any)) {
        return (maybeRef as any).value as T
      }
      return (maybeRef as T) ?? null
    }

    function getCampaignsList(): Campaings[] {
      const raw = resolveMaybeRef<Campaings[] | Record<string, Campaings> | null>(
        sessionStore.currentCampaings
      )

      if (Array.isArray(raw)) return raw
      if (raw && typeof raw === 'object') return Object.values(raw)
      return []
    }

    function getCurrentPageModules(): string[] {
      const currentRoute = router.currentRoute.value
      const routeRecordWithModule = [...currentRoute.matched]
        .reverse()
        .find((record) => Boolean(record.meta?.module))

      const rawModule = routeRecordWithModule?.meta?.module ?? currentRoute.meta?.module

      if (!rawModule) {
        return []
      }

      if (Array.isArray(rawModule)) {
        return rawModule.map((moduleCode) => String(moduleCode))
      }

      return [String(rawModule)]
    }

    function getCampaignModuleCodes(campaign: Campaings | null): string[] {
      if (!campaign || !Array.isArray(campaign.modulos)) {
        return []
      }

      return campaign.modulos
        .map((moduleItem) => String(moduleItem?.codigo))
        .filter(Boolean)
    }

    function getCurrentCampaignModule(
      campaign: Campaings | null,
      currentModules: string[] = getCurrentPageModules()
    ) {
      if (!campaign || !Array.isArray(campaign.modulos)) {
        return null
      }

      if (!currentModules.length) {
        return null
      }

      return (
        campaign.modulos.find((moduleItem) =>
          currentModules.includes(String(moduleItem?.codigo))
        ) ?? null
      )
    }

    function toNumber(value: unknown): number {
      const parsedValue = Number(value)
      return Number.isFinite(parsedValue) ? parsedValue : 0
    }

    function isCampaignModuleQuotaExceeded(
      moduleItem: Campaings['modulos'][number] | null | undefined
    ): boolean {
      if (!moduleItem) {
        return false
      }

      const limit = toNumber(moduleItem?.pivot?.cota_uso)
      const usage = toNumber(
        moduleItem?.pivot?.cota_usada_total ?? moduleItem?.pivot?.cota_usada
      )

      if (limit <= 0) {
        return false
      }

      return usage >= limit
    }

    function isAtHomePage() {
      const currentRoute = router.currentRoute.value
      return currentRoute.name === 'Home' || currentRoute.path === '/'
    }

    function getCampaignsForCurrentPage() {
      const campaings = getCampaignsList()
      if (!campaings.length) {
        return []
      }

      if (isAtHomePage()) {
        return campaings
      }

      const currentModules = getCurrentPageModules()
      if (!currentModules.length) {
        return []
      }

      return campaings.filter((campaign) =>
        getCampaignModuleCodes(campaign).some((moduleCode) =>
          currentModules.includes(moduleCode)
        )
      )
    }

    function getCampaignPrimaryAction(
      moduleCode: string | null | undefined,
      atHomePage: boolean,
      isQuotaExceeded = false,
      campaingId: number
    ): CampaignPrimaryAction {
      const normalizedModuleCode = String(moduleCode || '').toUpperCase()
      const returnparams: CampaignPrimaryAction = {
        primaryLabel: 'Ver Planos',
        onPrimaryAction: () => {
          const routeParam = { name: 'Planos', state: { exceededCampaingId: campaingId }};
          router.push(routeParam)
        },
      }
      if (isQuotaExceeded) {
        return returnparams
      }

      if(atHomePage){
        if (normalizedModuleCode === ModulosEnum.SERASA) {
          returnparams.primaryLabel = 'Fazer Consulta Serasa';
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/serasa' })
          }
        }else if (normalizedModuleCode === ModulosEnum.HIGIENIZACAO) {
          returnparams.primaryLabel = 'Fazer Higienização';
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/higienizacao' })
          };
        }else if (normalizedModuleCode === ModulosEnum.GESTAO_FLUXO_DE_CAIXA_FLUXO_CAIXA) {
          returnparams.primaryLabel = 'Ir Para Fluxo de Caixa'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/financeiro/fluxo-de-caixa' })
          }
        }else if (normalizedModuleCode === ModulosEnum.COMUNICACAO_EM_LOTE) {
          returnparams.primaryLabel = 'Fazer Comunicação Em Lote'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/comunicacao-em-lote/whatsapp' })
          }
        }else if (normalizedModuleCode === ModulosEnum.COMUNICACAO_EM_LOTE_SMS) {
          returnparams.primaryLabel = 'Fazer Comunicação Em Lote SMS'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/comunicacao-em-lote/sms' })
          }
        }else if (normalizedModuleCode === ModulosEnum.COMUNICACAO_EM_LOTE_WHATSAPP) {
          returnparams.primaryLabel = 'Fazer Comunicação Em Lote WhatsApp'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/comunicacao-em-lote/whatsapp' })
          }
        }else if (normalizedModuleCode === ModulosEnum.COMUNICACAO_EM_LOTE_EMAIL) {
          returnparams.primaryLabel = 'Fazer Comunicação Em Lote Email'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/comunicacao-em-lote/email' })
          }
        }else if (normalizedModuleCode === ModulosEnum.SERASA_NEGATIVACAO) {
          returnparams.primaryLabel = 'Fazer Negativação'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/negativacao' })
          }
        }else if (normalizedModuleCode === ModulosEnum.NOTAS_FISCAIS) {
          returnparams.primaryLabel = 'Emitir Nota Fiscal'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/notas-fiscais' })
          }
        }else if (normalizedModuleCode === ModulosEnum.AUTOMACAO_COMUNICACAO_COBRANCAS) {
          returnparams.primaryLabel = 'Automatizar Cobranças'
          returnparams.onPrimaryAction = () => {
            router.push({ path: '/automatizacao-cobranca' })
          }
        }
      }

      return returnparams
    }
    
    const dynamicNotifications = computed(() => {
      const notifications: GlobalNotification[] = []
      const hasLoadedCampaigns = resolveMaybeRef(sessionStore.currentCampaings) !== null

      // While campaign data is still loading, avoid showing fallback notifications.
      if (!hasLoadedCampaigns) {
        return notifications
      }

      const currentModules = getCurrentPageModules()
      const isHomePage = isAtHomePage()
      const pageCampaigns = getCampaignsForCurrentPage()

      if (pageCampaigns && pageCampaigns.length > 0) {
        pageCampaigns.forEach((campaing) => {
          const currentCampaignModule = isHomePage
            ? campaing.modulos?.[0] ?? null
            : getCurrentCampaignModule(campaing, currentModules)
          const isQuotaExceeded = isCampaignModuleQuotaExceeded(currentCampaignModule)
          const primaryAction = getCampaignPrimaryAction(
            currentCampaignModule?.codigo,
            isHomePage,
            isQuotaExceeded,
            campaing.id
          )

          notifications.push({
            id: `campaing-anounce-${campaing.id}-${currentCampaignModule?.codigo ?? 'general'}`,
            message: '',
            icon: CircleAlert,
            persistent: true,
            pushLayout: true,
            customComponent: CampanhaBanner,
            customComponentProps: {
              campaignId: campaing.id,
              campaingModuleCode: currentCampaignModule?.codigo,
              moduleNames: campaing.modulos
                ?.map((moduleItem) => moduleItem.titulo || moduleItem.nome)
                .filter((name): name is string => Boolean(name)),
              primaryLabel: primaryAction.primaryLabel,
              onPrimaryAction: primaryAction.onPrimaryAction,
            },
          })
        })

        return notifications.filter((n) => !dismissedNotifications.value.includes(n.id))
      } else {
        if ((sessionStore?.currentEmpresa?.assinatura?.is_waiting_for_finalization ?? false)) {
          console.log(sessionStore?.currentEmpresa?.assinatura?.is_waiting_for_finalization)
          if (localStorage.getItem('ass_waiting_finalization-dismissed') === 'true') {
            notifications.filter((n) => n.id !== 'ass_waiting_finalization')
          }else{
            const dataExpiracao = calcularDataExpiracao(sessionStore?.currentEmpresa?.assinatura?.criado_em)
            const diasRestantes = dataExpiracao ? calcularDiasRestantes(dataExpiracao) : 0
            const dataFormatada = dataExpiracao ? formatarDataExpiracao(dataExpiracao) : 'data indisponível'
            const diasTexto = diasRestantes <= 0 ? 'menos de 24 horas restantes' : `${diasRestantes} dias restantes`
            
            notifications.push({
              id: 'ass_waiting_finalization',
              message: `<strong>O Cancelamento do seu plano já está agendado!</strong> Seu plano expira em ${dataFormatada} (${diasTexto}).`,
              severity: 'error',
              icon: AlertCircleIcon,
              persistent: true,
              pushLayout: true,
              actions: [{
                  label: 'X',
                  severity: 'danger',
                  action: () => {
                    removeNotification('ass_waiting_finalization')
                    localStorage.setItem('ass_waiting_finalization-dismissed', 'true');
                  },
              }],
            })
          }
        }
        const planoAgendado = sessionStore?.currentEmpresa?.assinatura?.plano_agendado
        if (planoAgendado?.data_plano_agendado) {
          if (localStorage.getItem(`plano-agendado-dismissed-${planoAgendado.uuid_pla_id}`) !== 'true') {
            const dataFormatada = moment(planoAgendado.data_plano_agendado).format('DD/MM/YYYY')

            notifications.push({
              id: 'plano-agendado-downgrade',
              message: `<strong>Downgrade Agendado!</strong> O seu plano mudará para o ${planoAgendado.pla_descricao} em ${dataFormatada}.`,
              severity: 'warn',
              icon: Clock,
              persistent: true,
              pushLayout: true,
              actions: [{
                label: 'X',
                severity: 'warn',
                action: () => {
                  removeNotification('plano-agendado-downgrade')
                  localStorage.setItem(`plano-agendado-dismissed-${planoAgendado.uuid_pla_id}`, 'true')
                },
              }],
            })
          }
        }
        if (sessionStore?.currentEmpresa?.assinatura?.is_waiting_for_recover_plan_payment) {
          notifications.push({
            id: 'recover-plan-payment',
            message:
              '<strong>Atenção:</strong> Há um pagamento pendente para recuperação do seu plano. Por favor, regularize para evitar interrupções no serviço.',
            severity: 'error',
            icon: CircleAlert,
            persistent: true,
            pushLayout: true,
            actions: [
              {
                label: 'Regularizar Pagamento',
                severity: 'danger',
                action: () => {
                  router.push({
                    path: `/planos/informacoes-de-cobranca`
                  })
                },
              },
            ],
          })
        } else if (sessionStore?.currentEmpresa?.assinatura?.bloqueada) {
          notifications.push({
            id: 'blocked-plan',
            message:
              '<strong>Falha no pagamento:</strong> Tentamos processar o pagamento no seu cartão cadastrado, mas não foi possível. Seu acesso está bloqueado até a regularização.',
            severity: 'error',
            icon: CircleAlert,
            persistent: true,
            pushLayout: true,
            actions: [
              {
                label: 'Regularizar Pagamento',
                severity: 'danger',
                action: () => {
                  router.push({
                    path: `/planos/informacoes-de-cobranca`
                  })
                },
              },
            ],
          })
        } else if (
          !sessionStore.isSubscriptionDefault &&
          !sessionStore.currentUser?.usu_seth &&
          !sessionStore.isLegacyCompanyExemptFromContractBlock &&
          router.currentRoute.value.name !== 'EditarEmpresa' &&
          !(
            Boolean(sessionStore.currentEmpresa?.empresa_documentacao?.edo_contrato_principal_status) &&
            Boolean(sessionStore.currentEmpresa?.empresa_documentacao?.edo_anexo_i_status)
          )
        ) {
          const empresaId = sessionStore.userData?.empresa?.uuid_emp_id

          notifications.push({
            id: 'contrato-licenca-pendente',
            message:
              '<strong>Assinatura de contrato pendente:</strong> A assinatura dos documentos de contrato e licença do plano é obrigatória.',
            severity: 'error',
            icon: CircleAlert,
            persistent: true,
            pushLayout: true,
            actions: empresaId
              ? [
                  {
                    label: 'Ver contrato',
                    severity: 'danger',
                    action: () => {
                      router.push({
                        path: `/cadastros/empresas/editar/${empresaId}`,
                        query: { tab: 'three' },
                      })
                    },
                  },
                ]
              : [],
          })
        } else if (sessionStore.isSubscriptionDefault) {
          const empresaId = sessionStore.userData?.empresa?.uuid_emp_id
          const documentacaoAprovada = Boolean(sessionStore.currentEmpresa?.emp_documentacao_status)
          const statusDocumentacao = sessionStore.currentEmpresa?.emp_status_documentacao

          if (!documentacaoAprovada && statusDocumentacao === EmpresaStatusDocumentacaoEnum.PENDENTE) {
            notifications.push({
              id: 'documentation-pending',
              message:
                '<strong>Complete seu Cadastro:</strong> Envie a sua documentação para liberar a contratação de planos.',
              severity: 'warn',
              icon: AlertTriangle,
              persistent: true,
              pushLayout: true,
              actions: empresaId
                ? [
                    {
                      label: 'Enviar Documentação',
                      severity: 'warn',
                      action: () => {
                        router.push({
                          path: `/cadastros/empresas/editar/${empresaId}`,
                          query: { tab: 'three' },
                        })
                      },
                    },
                  ]
                : [],
            })
          } else if (
            !documentacaoAprovada &&
            statusDocumentacao === EmpresaStatusDocumentacaoEnum.AGUARDANDO_APROVACAO
          ) {
            notifications.push({
              id: 'documentation-awaiting-approval',
              message:
                '<strong>Documentação em Análise:</strong> Nossa equipe está analisando seus documentos. Aguarde a aprovação para contratar um plano.',
              severity: 'info',
              icon: CircleAlert,
              persistent: true,
              pushLayout: true,
              actions: [],
            })
          } else if (!documentacaoAprovada && statusDocumentacao === EmpresaStatusDocumentacaoEnum.RECUSADO) {
            notifications.push({
              id: 'documentation-rejected',
              message:
                '<strong>Reenvie sua Documentação:</strong> Alguns documentos foram rejeitados e precisam ser reenviados.',
              severity: 'error',
              icon: CircleAlert,
              persistent: true,
              pushLayout: true,
              actions: empresaId
                ? [
                    {
                      label: 'Reenviar Documentos',
                      severity: 'danger',
                      action: () => {
                        router.push({
                          path: `/cadastros/empresas/editar/${empresaId}`,
                          query: { tab: 'three' },
                        })
                      },
                    },
                  ]
                : [],
            })
          } else if (documentacaoAprovada) {
            notifications.push({
              id: 'plan-pending',
              message:
                '<strong>Documentação Aprovada!</strong> Contrate um plano para utilizar todas as funcionalidades da Seth.',
              severity: 'success',
              icon: AlertTriangle,
              persistent: true,
              pushLayout: true,
              actions: [
                {
                  label: 'Ver Planos',
                  severity: 'success',
                  action: () => {
                    router.push({
                      path: `/planos`,
                    })
                  },
                },
              ],
            })
          } else {
            notifications.push({
              id: 'plan-pending-default',
              message:
                '<strong>Pronto para começar?</strong> Escolha um plano e tenha acesso completo a todas as funcionalidades da Seth.',
              severity: 'warn',
              icon: AlertTriangle,
              persistent: true,
              pushLayout: true,
              actions: [
                {
                  label: 'Ver Planos',
                  severity: 'warn',
                  action: () => {
                    router.push({
                      path: `/planos`,
                    })
                  },
                },
              ],
            })
          }
        }

        return notifications.filter((n) => !dismissedNotifications.value.includes(n.id))
      }
    })

    const notifications = computed(() => [
      ...staticNotifications.value,
      ...dynamicNotifications.value,
    ])

    function addNotification(notification: Omit<GlobalNotification, 'id'>) {
      const newNotification: GlobalNotification = {
        ...notification,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      }

      staticNotifications.value.push(newNotification)

      // Remove automaticamente se não for persistente
      if (!notification.persistent) {
        setTimeout(() => {
          removeNotification(newNotification.id)
        }, 5000)
      }
    }

    function removeNotification(id: string) {
      const index = staticNotifications.value.findIndex((n) => n.id === id)
      if (index > -1) {
        staticNotifications.value.splice(index, 1)
      }
      // If not found among static notifications, treat it as a dynamic notification
      // and mark it as dismissed so computed dynamic notifications stop showing it.
      if (!dismissedNotifications.value.includes(id)) {
        dismissedNotifications.value.push(id)
      }
    }

    function clearNotifications() {
      staticNotifications.value = []
    }

    return {
      notifications,
      addNotification,
      removeNotification,
      clearNotifications,
    }
  }
)
